import React, { useState, useEffect, useRef } from 'react';

import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaToggleOn,
  FaToggleOff,
  FaFileExcel,
  FaPaperPlane,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';

import { FiFilter, FiX, FiRefreshCw, FiSave } from 'react-icons/fi';
import { Editor } from '@tinymce/tinymce-react';
import validator from 'validator';
import axios from 'axios';
import axiosInstance from '../../../libs/axios';
import { PaginationType, Pagination as PaginationUtils } from '../../../utils/pagination.utils';
import { ExcelDownload } from '../../../utils/excel.utils';
import { useAuth } from '../../../provider/auth.provider';

interface Template {
  id: number;
  name: string;
  subject?: string;
  title?: string;
  message: string;
  type: 'email' | 'sms' | 'push';
  event?: string;
  variables: string[];
  isActive: boolean;
  attachments?: string[];
  created_at: string;
  updated_at: string;
}

interface Filters {
  search?: string;
  type?: string;
  event?: string;
  isActive?: string;
}

const TemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit' | 'preview'>('add');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const editorRef = useRef<any>(null);

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    title: '',
    message: '',
    type: 'email' as 'email' | 'sms' | 'push',
    event: '',
    variables: '',
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attachments, setAttachments] = useState<File[]>([]);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewVars, setPreviewVars] = useState<Record<string, string>>({});
  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    limit: 10,
    total_count: 0,
    total_pages: 0,
    offset: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  });
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' }>({
    field: 'created_at',
    direction: 'DESC',
  });
  const [exporting, setExporting] = useState(false);
  const [exportData, setExportData] = useState<any[]>([]);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async (page?: number, limit?: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const effectivePage = page ?? pagination.page ?? 1;
      const effectiveLimit = limit ?? pagination.limit ?? 10;
      params.append('page', String(effectivePage));
      params.append('limit', String(effectiveLimit));

      // Apply server-side sorting
      if (sortConfig?.field) {
        params.append('sort_by', sortConfig.field);
        params.append('sort_order', sortConfig.direction);
      }

      const response = await axiosInstance.get(`/notification/templates?${params.toString()}`);
      if (response.data.success) {
        const data = response.data.data;
        if (data?.templates) {
          const normalizedTemplates: Template[] = data.templates.map((tpl: any) => {
            if (Array.isArray(tpl.variables)) {
              return tpl as Template;
            }

            if (!tpl.variables) {
              return { ...tpl, variables: [] } as Template;
            }

            let raw = String(tpl.variables).trim();

            if (raw.startsWith('[') && raw.endsWith(']')) {
              raw = raw.slice(1, -1);
            }

            const vars = raw
              .split(',')
              .map((v: string) => v.trim().replace(/^['"]+|['"]+$/g, ''))
              .filter((v: string) => v);

            return { ...tpl, variables: vars } as Template;
          });

          setTemplates(normalizedTemplates);

          if (data.pagination) {
            setPagination(prev => ({
              ...prev,
              ...data.pagination,
              page: data.pagination.page ?? effectivePage,
              limit: data.pagination.limit ?? effectiveLimit,
            }));
          } else {
            setPagination(prev => ({ ...prev, page: effectivePage, limit: effectiveLimit }));
          }
        } else {
          setTemplates(data || []);
        }
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name || validator.isEmpty(formData.name.trim())) {
      newErrors.name = 'Template name is required';
    }

    if (formData.type === 'email' && (!formData.subject || validator.isEmpty(formData.subject.trim()))) {
      newErrors.subject = 'Subject is required for email templates';
    }

    if (formData.type === 'push' && (!formData.title || validator.isEmpty(formData.title.trim()))) {
      newErrors.title = 'Title is required for push notifications';
    }

    if (!formData.message || validator.isEmpty(formData.message.trim())) {
      newErrors.message = 'Message is required';
    }

    if (formData.variables) {
      const vars = formData.variables.split(',').map(v => v.trim()).filter(v => v);
      for (const v of vars) {
        if (!validator.isAlphanumeric(v.replace(/_/g, ''))) {
          newErrors.variables = 'Variables must contain only letters, numbers, and underscores';
          break;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const data: any = {
        name: formData.name,
        message: formData.message,
        type: formData.type,
        isActive: formData.isActive,
        variables: formData.variables.split(',').map(v => v.trim()).filter(v => v)
      };

      if (formData.type === 'email') data.subject = formData.subject;
      if (formData.type === 'push') data.title = formData.title;
      if (formData.event) data.event = formData.event;

      if (attachments.length > 0) {
        const formDataObj = new FormData();
        Object.entries(data).forEach(([key, value]) => {
          if (key === 'variables') {
            formDataObj.append(key, JSON.stringify(value));
          } else {
            formDataObj.append(key, String(value));
          }
        });
        attachments.forEach(file => {
          formDataObj.append('attachments', file);
        });

        if (modalMode === 'edit' && selectedTemplate) {
          await axiosInstance.put(`/notification/templates/${selectedTemplate.id}`, formDataObj, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } else {
          await axiosInstance.post('/notification/templates', formDataObj, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
      } else {
        if (modalMode === 'edit' && selectedTemplate) {
          await axiosInstance.put(`/notification/templates/${selectedTemplate.id}`, data);
        } else {
          await axiosInstance.post('/notification/templates', data);
        }
      }

      fetchTemplates();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    setLoading(true);
    try {
      await axiosInstance.delete(`/notification/templates/${selectedTemplate.id}`);
      fetchTemplates();
      setShowDeleteModal(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error deleting template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (template: Template) => {
    setLoading(true);
    try {
      await axiosInstance.patch(`/notification/templates/${template.id}/toggle-status`);
      fetchTemplates();
    } catch (error) {
      console.error('Error toggling status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (template: Template) => {
    setSelectedTemplate(template);
    setModalMode('preview');
    setShowModal(true);

    const vars: Record<string, string> = {};
    template.variables.forEach(v => {
      vars[v] = '';
    });
    setPreviewVars(vars);
  };

  const generatePreview = async () => {
    if (!selectedTemplate) return;

    setLoading(true);
    try {
      const response = await axiosInstance.post(
        `/notification/templates/${selectedTemplate.id}/preview`,
        { variables: previewVars }
      );
      if (response.data.success) {
        setPreviewData(response.data.data);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode: 'add' | 'edit', template?: Template) => {
    setModalMode(mode);
    if (mode === 'edit' && template) {
      setSelectedTemplate(template);
      setFormData({
        name: template.name,
        subject: template.subject || '',
        title: template.title || '',
        message: template.message,
        type: template.type,
        event: template.event || '',
        variables: template.variables.join(', '),
        isActive: template.isActive
      });
    } else {
      setFormData({
        name: '',
        subject: '',
        title: '',
        message: '',
        type: 'email',
        event: '',
        variables: '',
        isActive: true
      });
    }
    setErrors({});
    setAttachments([]);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTemplate(null);
    setPreviewData(null);
    setPreviewVars({});
    setFormData({
      name: '',
      subject: '',
      title: '',
      message: '',
      type: 'email',
      event: '',
      variables: '',
      isActive: true
    });
    setErrors({});
    setAttachments([]);
  };

  const applyFilters = () => {
    // Deprecated: filters now auto-apply via debounced effect
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchTemplates(1, pagination.limit);
  };

  const resetFilters = () => {
    setFilters({});
  };

  // Debounced auto-fetch on filters change (search, type, event, status)
  const isFirstFiltersEffect = useRef(true);
  useEffect(() => {
    if (isFirstFiltersEffect.current) {
      isFirstFiltersEffect.current = false;
      return;
    }

    const handler = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchTemplates(1, pagination.limit);
    }, 400);

    return () => clearTimeout(handler);
  }, [filters, pagination.limit]);

  // Refetch when sort changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchTemplates(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortConfig]);

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
    fetchTemplates(page, pagination.limit);
  };

  const handleLimitChange = (limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
    fetchTemplates(1, limit);
  };

  const handleSort = (field: string) => {
    setSortConfig(prev => {
      const direction = prev.field === field && prev.direction === 'ASC' ? 'DESC' : 'ASC';
      return { field, direction };
    });
  };

  const renderSortIcon = (field: string) => {
    if (sortConfig.field !== field) {
      return <FaSort className="w-3 h-3 text-gray-400" />;
    }

    if (sortConfig.direction === 'ASC') {
      return <FaSortUp className="w-3 h-3 text-blue-600" />;
    }

    return <FaSortDown className="w-3 h-3 text-blue-600" />;
  };

  const fetchExportData = async () => {
    try {
      setExporting(true);

      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      // For export, fetch a large page with current sort
      params.append('page', '1');
      params.append('limit', '10000');

      if (sortConfig?.field) {
        params.append('sort_by', sortConfig.field);
        params.append('sort_order', sortConfig.direction);
      }

      const response = await axiosInstance.get(`/notification/templates?${params.toString()}`);
      if (response.data?.success) {
        const data = response.data.data;
        const allTemplates: any[] = data?.templates || data || [];

        const mapped = allTemplates.map((tpl: any) => ({
          Name: tpl.name || '',
          Type: tpl.type || '',
          Event: tpl.event || '',
          Title: tpl.title || '',
          Subject: tpl.subject || '',
          Message: tpl.message || '',
          Variables: Array.isArray(tpl.variables)
            ? tpl.variables.join(', ')
            : typeof tpl.variables === 'string'
              ? tpl.variables
              : '',
          Attachments: Array.isArray(tpl.attachment_urls)
            ? tpl.attachment_urls.join(', ')
            : Array.isArray(tpl.attachments)
              ? tpl.attachments.join(', ')
              : '',
          Status: tpl.is_active ? 'Active' : 'Inactive',
          CreatedBy: tpl.creator
            ? `${tpl.creator.first_name || ''} ${tpl.creator.last_name || ''}`.trim()
            : '',
          UpdatedBy: tpl.updater
            ? `${tpl.updater.first_name || ''} ${tpl.updater.last_name || ''}`.trim()
            : '',
          CreatedAt: tpl.created_at || '',
          UpdatedAt: tpl.updated_at || '',
        }));

        setExportData(mapped);
        setShowExcelModal(true);
      }
    } catch (error) {
      console.error('Error exporting templates:', error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Template Management</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchExportData}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded flex items-center justify-center gap-2 transition-colors text-sm font-medium shadow-sm"
            >
              <FaFileExcel className="w-4 h-4" />
              {exporting ? 'Preparing...' : 'Export Excel'}
            </button>
            {hasPermission('notification_template', 'create') && (
              <button
                onClick={() => handleOpenModal('add')}
                className="bg-blue-800 hover:bg-blue-950 text-white px-4 py-2 rounded flex items-center gap-2 transition-colors text-sm font-medium shadow-sm"
              >
                <FaPlus className="w-4 h-4" />
                Add Template
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/30">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Search templates..."
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filters.search || ''}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <select
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filters.type || ''}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          >
            <option value="">All Types</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="push">Push</option>
          </select>
          <input
            type="text"
            placeholder="Event..."
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filters.event || ''}
            onChange={(e) => setFilters({ ...filters, event: e.target.value })}
          />
          {/* 
          <select
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={filters.isActive || ''}
            onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          */}
        </div>
        <div className="flex gap-2 mt-3">
          {/* 
          <button
            onClick={applyFilters}
            className="px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 text-sm flex items-center gap-2"
          >
            <FiFilter className="w-4 h-4" />
            Apply
          </button>
          <button
            onClick={resetFilters}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm flex items-center gap-2"
          >
            <FiRefreshCw className="w-4 h-4" />
            Reset
          </button>
          */}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Actions</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                <button
                  type="button"
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-1 group"
                >
                  <span>Name</span>
                  {renderSortIcon('name')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                <button
                  type="button"
                  onClick={() => handleSort('type')}
                  className="flex items-center gap-1 group"
                >
                  <span>Type</span>
                  {renderSortIcon('type')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                <button
                  type="button"
                  onClick={() => handleSort('event')}
                  className="flex items-center gap-1 group"
                >
                  <span>Event</span>
                  {renderSortIcon('event')}
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Variables</th>
              {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th> */}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                </td>
              </tr>
            ) : templates.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                  No templates found
                </td>
              </tr>
            ) : (
              templates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex gap-2">
                      {hasPermission('notification_template', 'view') && (
                        <button
                          onClick={() => handlePreview(template)}
                          className="text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded"
                          title="Preview"
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                      )}
                      {hasPermission('notification_template', 'update') && (
                        <button
                          onClick={() => handleOpenModal('edit', template)}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {hasPermission('notification_template', 'delete') && (
                        <button
                          onClick={() => {
                            setSelectedTemplate(template);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">{template.name}</td>
                  <td className="px-4 py-4">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {template.type}
                    </span>
                  </td>
                  <td className="px-4 py-4">{template.event || '-'}</td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {template.variables.map((v, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                          {v}
                        </span>
                      ))}
                    </div>
                  </td>
                  {/* 
                  <td className="px-4 py-4">
                    <button
                      onClick={() => handleToggleStatus(template)}
                      className="flex items-center gap-1"
                    >
                      {template.isActive ? (
                        <FaToggleOn className="w-6 h-6 text-green-600" />
                      ) : (
                        <FaToggleOff className="w-6 h-6 text-gray-400" />
                      )}
                    </button>
                  </td>
                  */}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <PaginationUtils
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />

      {/* Excel Download Modal */}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
      />

      {showModal && modalMode !== 'preview' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">
                  {modalMode === 'add' ? 'Add Template' : 'Edit Template'}
                </h2>
                <button onClick={handleCloseModal} className="text-white hover:text-gray-200">
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template Name *</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="push">Push</option>
                  </select>
                </div>

                {formData.type === 'email' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.subject ? 'border-red-500' : 'border-gray-300'
                        }`}
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    />
                    {errors.subject && <p className="text-red-500 text-xs mt-1">{errors.subject}</p>}
                  </div>
                )}

                {formData.type === 'push' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                    <input
                      type="text"
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.title ? 'border-red-500' : 'border-gray-300'
                        }`}
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                    {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Event</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={formData.event}
                    onChange={(e) => setFormData({ ...formData, event: e.target.value })}
                    placeholder="e.g., register, login"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Variables (comma-separated)</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.variables ? 'border-red-500' : 'border-gray-300'
                      }`}
                    value={formData.variables}
                    onChange={(e) => setFormData({ ...formData, variables: e.target.value })}
                    placeholder="user_name, email"
                  />
                  {errors.variables && <p className="text-red-500 text-xs mt-1">{errors.variables}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  {formData.type === 'email' ? (
                    <Editor
                      apiKey="eyb5ds77fevohhz729v0d975lfavgn5cgoqf0wk68x48wnu7"
                      onInit={(evt, editor) => (editorRef.current = editor)}
                      value={formData.message}
                      onEditorChange={(content) => setFormData({ ...formData, message: content })}
                      init={{
                        height: 300,
                        menubar: false,
                        plugins: ['lists', 'link', 'code'],
                        toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright | bullist numlist | link code'
                      }}
                    />
                  ) : (
                    <textarea
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.message ? 'border-red-500' : 'border-gray-300'
                        }`}
                      rows={6}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    />
                  )}
                  {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                </div>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>

                {formData.type === 'email' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
                    <input
                      type="file"
                      multiple
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onChange={(e) => setAttachments(Array.from(e.target.files || []))}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-900 flex items-center gap-2 disabled:opacity-50"
              >
                <FiSave className="w-4 h-4" />
                {loading ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && modalMode === 'preview' && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-800 to-blue-900">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">Preview Template</h2>
                <button onClick={handleCloseModal} className="text-white hover:text-gray-200">
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <h3 className="font-medium mb-4">Enter Variable Values:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {selectedTemplate.variables.map((variable) => (
                  <div key={variable}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{variable}</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={previewVars[variable] || ''}
                      onChange={(e) => setPreviewVars({ ...previewVars, [variable]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
              {hasPermission('notification_template', 'preview') && (
                <button
                  onClick={generatePreview}
                  className="mb-4 px-4 py-2 bg-blue-800 text-white rounded hover:bg-blue-900 flex items-center gap-2"
                >
                  <FaEye className="w-4 h-4" />
                  Generate Preview
                </button>
              )}
              {previewData && (
                <div className="border border-gray-300 rounded p-4 bg-gray-50">
                  <h3 className="font-medium mb-2">Preview:</h3>
                  {selectedTemplate.type === 'email' && previewData.subject && (
                    <div className="mb-2">
                      <strong>Subject:</strong> {previewData.subject}
                    </div>
                  )}
                  {selectedTemplate.type === 'push' && previewData.title && (
                    <div className="mb-2">
                      <strong>Title:</strong> {previewData.title}
                    </div>
                  )}
                  <div>
                    <strong>Message:</strong>
                    <div dangerouslySetInnerHTML={{ __html: previewData.message }} />
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Delete Template</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-600">
                Are you sure you want to delete this template? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedTemplate(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManagement;